import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const AnimatedCounter = ({ 
  end, 
  duration = 2000, // duration in milliseconds
  className = "", 
  suffix = "", 
  prefix = "",
  decimals = 0,
  delay = 0 // delay before starting animation
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Handle special string cases (like "24/7")
    if (typeof end === 'string' && end.includes('/')) {
      setDisplayValue(end);
      return;
    }

    // Parse numeric value
    const targetValue = typeof end === 'string' ? parseFloat(end) : Number(end);
    
    // If not a valid number, display the original value
    if (isNaN(targetValue)) {
      setDisplayValue(end);
      return;
    }

    // Start animation after delay
    const startAnimation = () => {
      setIsAnimating(true);
      const startTime = Date.now();
      const startValue = 0;

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentValue = startValue + (targetValue - startValue) * easeOutCubic;
        
        setDisplayValue(decimals > 0 ? currentValue : Math.floor(currentValue));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setDisplayValue(targetValue);
          setIsAnimating(false);
        }
      };

      requestAnimationFrame(animate);
    };

    const timer = setTimeout(startAnimation, delay);
    return () => clearTimeout(timer);
  }, [end, duration, decimals, delay]);

  // Format the display value
  const formatValue = () => {
    // If displayValue is a string (like "24/7"), return it directly
    if (typeof displayValue === 'string') {
      return displayValue;
    }
    
    // For numeric values, format and apply prefix and suffix
    const formattedValue = decimals > 0 ? displayValue.toFixed(decimals) : Math.floor(displayValue);
    return `${prefix}${formattedValue}${suffix}`;
  };

  return (
    <motion.div
      className={className}
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ 
        duration: 0.6,
        type: "spring",
        stiffness: 100,
        damping: 10,
        delay: delay / 1000
      }}
    >
      <motion.span
        animate={isAnimating ? { 
          textShadow: [
            '0 0 0px rgba(159, 203, 220, 0)',
            '0 0 10px rgba(159, 203, 220, 0.5)',
            '0 0 0px rgba(159, 203, 220, 0)'
          ]
        } : {}}
        transition={{ duration: 2, repeat: Infinity }}
      >
        {formatValue()}
      </motion.span>
    </motion.div>
  );
};

export default AnimatedCounter;